// src/services/billingApi.ts
//
// WS-C — Tiny client for /api/billing/* (Netlify function `billing.ts`).
// Read-only for now; CardKnox payment-method write path lands in WS-C-followup.

import axios, { type AxiosInstance } from 'axios';

const BASE = '/api/billing';

export interface InvoiceListRow {
  id: string;
  invoice: string;
  date: string;
  due_date: string | null;
  description: string | null;
  amountbilled: number;
  amountpaid: number;
  balance: number;
  paiddate: string | null;
  pdf_url: string | null;
  customername: string | null;
  invoice_type?: 'invoice' | 'credit_memo' | null;
  app_id?: string | null;
}

export interface BillingOverview {
  hasBillingSetup: boolean;
  customerName?: string | null;
  totalBilled: number;
  totalPaid: number;
  outstanding: number;
  recent: InvoiceListRow[];
  defaultPaymentMethod: PaymentMethodSummary | null;
}

export interface PaymentMethodSummary {
  id?: string;
  paymentMethodId: string;
  maskedCardNumber: string | null;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  nameOnCard: string | null;
  isDefault?: boolean;
}

export interface InvoiceDetail {
  invoice: InvoiceListRow & { [k: string]: unknown };
  lines: Array<{
    id?: string;
    description?: string | null;
    quantity?: number | null;
    unit_price?: number | null;
    amount?: number | null;
    [k: string]: unknown;
  }>;
}

interface Envelope<T> { success: boolean; data?: T; error?: string; }

function buildClient(token?: string | null, slug?: string | null): AxiosInstance {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (slug) headers['x-tenant-slug'] = slug;
  return axios.create({ baseURL: '', timeout: 30000, headers });
}

class BillingApiService {
  private _client: AxiosInstance;
  private _token: string | null = null;
  private _slug: string | null = null;

  constructor() { this._client = buildClient(); }

  setAuthToken(token: string | null) { this._token = token; this._client = buildClient(this._token, this._slug); }
  setTenantSlug(slug: string | null) { this._slug = slug;  this._client = buildClient(this._token, this._slug); }

  async getOverview(): Promise<BillingOverview> {
    const r = await this._client.get<Envelope<BillingOverview>>(`${BASE}/overview`);
    return this.unwrap(r.data);
  }

  async listInvoices(): Promise<InvoiceListRow[]> {
    const r = await this._client.get<Envelope<InvoiceListRow[]>>(`${BASE}/invoices`);
    return this.unwrap(r.data);
  }

  async getInvoice(id: string): Promise<InvoiceDetail> {
    const r = await this._client.get<Envelope<InvoiceDetail>>(`${BASE}/invoices/${encodeURIComponent(id)}`);
    return this.unwrap(r.data);
  }

  async listPaymentMethods(): Promise<PaymentMethodSummary[]> {
    const r = await this._client.get<Envelope<PaymentMethodSummary[]>>(`${BASE}/payment-methods`);
    return this.unwrap(r.data);
  }

  private unwrap<T>(env: Envelope<T>): T {
    if (!env.success) throw new Error(env.error || 'Billing API request failed');
    return env.data as T;
  }
}

export const billingApi = new BillingApiService();
